import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { SessionUser } from '@cb-manager/shared';

import { auth, type ApiError, type MeResponse } from '../api/client';
import type { Role } from './roles';
import { parseRole } from './roles';
import {
  cabinetScopeOf,
  readProviderMarker,
  resolveSessionProvider,
  type SessionProvider,
} from './session-provider';

/**
 * Everything the UI is allowed to know about the session, fetched once on boot
 * (`GET /auth/me` through TanStack Query) and shared through context.
 *
 * Derivations live here so no page re-invents them:
 *  - `status`    — 'loading' | 'ready' | 'anonymous'. `authDisabled` (local dev
 *                  / e2e) counts as 'ready': there is no login flow to bounce
 *                  through, the api synthesises the local user.
 *  - `role`      — from `/auth/me`; `null` until the T.B1 RBAC upgrade lands.
 *                  Pages gate their own edit controls off it (`hasAtLeast`).
 *  - `provider`  — ADDITIVE (wave 9, T.Y16): where the session came from — the
 *                  `authProvider` field on `/auth/me`, with the sessionStorage
 *                  marker (`./session-provider`) as the fallback. Drives the
 *                  cabinet nav item; `null` behaves as a staff session.
 */
export interface AuthContextValue {
  status: 'loading' | 'ready' | 'anonymous';
  user: SessionUser | null;
  role: Role | null;
  authDisabled: boolean;
  /** ADDITIVE (wave 9) — `'email_code'` for a cabinet-only session, else `null`. */
  provider: SessionProvider | null;
  /**
   * ADDITIVE (wave 10, D21) — the effective cabinet scope the api published
   * (`user.cabinetVenueIds`): the venues #26–#26.6 answer for THIS session. Empty for
   * a session the cabinet is closed to; the nav and the cabinet page read it.
   */
  cabinetVenueIds: ReadonlyArray<number>;
  /** Set when /auth/me failed; a 401 is the normal "not signed in" answer. */
  error: ApiError | null;
  query: UseQueryResult<MeResponse, ApiError>;
  refetch: () => void;
}

/** Normalises where the role sits in the payload (top level or inside `user`). */
export function readMe(body: MeResponse | undefined): {
  user: SessionUser | null;
  role: Role | null;
  authDisabled: boolean;
  cabinetVenueIds: ReadonlyArray<number>;
} {
  if (!body) return { user: null, role: null, authDisabled: false, cabinetVenueIds: [] };
  return {
    user: body.user ?? null,
    role: parseRole(body.role) ?? parseRole(body.user?.role) ?? null,
    authDisabled: body.authDisabled === true,
    cabinetVenueIds: cabinetScopeOf({
      cabinetVenueIds: body.user?.cabinetVenueIds,
      curatorVenueIds: body.user?.curatorVenueIds,
    }),
  };
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // One query for the whole app — UserChip, the router guard and every page
  // read the same cache entry, so /auth/me is fetched exactly once per boot.
  const query = useQuery<MeResponse, ApiError>({
    queryKey: ['auth', 'me'],
    queryFn: async ({ signal }) => (await auth.me(signal)).data,
    staleTime: 60_000,
    retry: false,
  });

  const value = useMemo<AuthContextValue>(() => {
    const { user, role, authDisabled: disabled, cabinetVenueIds } = readMe(query.data);
    const ready = disabled || query.data?.authenticated === true;
    return {
      status: query.isPending ? 'loading' : ready ? 'ready' : 'anonymous',
      user,
      role,
      authDisabled: disabled,
      cabinetVenueIds,
      provider: resolveSessionProvider({
        authDisabled: disabled,
        // T.Y16 — the api publishes the session's provider; the marker is only the
        // fallback for deploy skew and the moment before this answer is refetched.
        meAuthProvider: query.data?.authProvider ?? query.data?.user?.authProvider,
        marker: readProviderMarker(typeof window === 'undefined' ? null : window.sessionStorage),
      }),
      error: query.error ?? null,
      query,
      refetch: () => void query.refetch(),
    };
  }, [query]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Session state of the current user. Throws outside an `<AuthProvider>`. */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth() must be used inside <AuthProvider>');
  return value;
}
